import type { MembershipLine } from './lines';
import type { Membership } from './types';

/**
 * One order of a `/v2/cart/calculate` answer, as far as memberships go. Both
 * fields are optional and read tolerantly: other tenants, the kill switch,
 * guests and zones without plans get no `membership` block at all.
 */
export interface CartMembershipOrder {
  membership?: unknown;
  membership_discount_total?: unknown;
}

/** The next discount step of a member below it. */
export interface CartMembershipNextTier {
  /** Order subtotal still missing to reach the step, in minor units. */
  missingMinor: number;
  /** Discount percent of the step. */
  percent: number;
}

/** What the cart shows about memberships. */
export type CartMembershipBenefits =
  | {
      kind: 'member';
      /** Member discount summed over every order, in minor units; `0` when none applies. */
      discountMinor: number;
      /** Percent of the applied step; `null` when the answer does not state it. */
      percent: number | null;
      /** `null` on the top step or when the answer does not state it. */
      nextTier: CartMembershipNextTier | null;
    }
  /**
   * calculate offers the zone's plan instead: the customer has no benefits
   * here — no membership, `past_due`, or a member of another zone. The club
   * row also checks `/me`, so only a customer with no membership is offered one.
   */
  | {
      kind: 'upsell';
      /** Plan Join opens with; `null` when the answer does not name it. */
      planId: string | null;
      /** Code of the offered plan; `null` when the answer does not name it. */
      planCode: string | null;
      /** The plan would make a paid delivery of this cart free. */
      wouldDeliveryBeFree: boolean;
      /** Delivery fees the plan would save over every delivery, in minor units; `0` when none. */
      deliverySavingMinor: number;
    }
  | { kind: 'none' };

/** Nothing to show. */
export const CART_MEMBERSHIP_NONE: CartMembershipBenefits = { kind: 'none' };

/** Formats an amount in minor units in the site currency. */
export type CartMoney = (amountMinor: number) => string;

/** The member discount row of the cart and checkout totals. */
export interface MemberDiscountRow {
  line: MembershipLine;
  /** Discount in minor units, shown with a minus. */
  amountMinor: number;
}

/** Payload of `Membership: Tier Hint Viewed`. */
export interface MembershipTierHintViewedPayload {
  /** Percent of the next step. */
  percent: number;
  /** Order subtotal still missing to reach it, in minor units. */
  missingMinor: number;
}

type Fields = Record<string, unknown>;

const asFields = (value: unknown): Fields | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Fields)
    : null;

const positive = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;

const nonEmptyText = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

/**
 * Reads the membership part of a calculate answer. Discounts of all orders
 * add up; the step, the hint and the offered plan come from the first order
 * that carries a `membership` block, while the offer's delivery saving adds
 * up over every delivery that has one and delivery turns free when it does
 * for any of them. `active: true` makes a member; otherwise an `upsell` makes
 * an offer; without either flag a step or a discount still marks a member.
 * `withDiscount` is `false` for converted prices: the member discount comes
 * in base-currency minor units those carts never show, so it reads as `0`
 * and the "Member discount" row is hidden.
 */
export const toCartMembershipBenefits = (
  orders: readonly CartMembershipOrder[],
  enabled: boolean,
  withDiscount = true,
): CartMembershipBenefits => {
  if (!enabled) {
    return CART_MEMBERSHIP_NONE;
  }

  const discountMinor = withDiscount
    ? orders.reduce(
        (sum, order) => sum + positive(order.membership_discount_total),
        0,
      )
    : 0;
  const blocks = orders
    .map((order) => asFields(order.membership))
    .filter((fields): fields is Fields => fields !== null);
  const block = blocks[0] ?? null;
  const applied = asFields(block?.appliedTier);
  const next = asFields(block?.nextTier);
  const upsell = asFields(block?.upsell);

  if (block?.active !== true && upsell) {
    const offers = blocks
      .map((fields) => asFields(fields.upsell))
      .filter((fields): fields is Fields => fields !== null);
    return {
      kind: 'upsell',
      planId: nonEmptyText(upsell.planId),
      planCode: nonEmptyText(upsell.planCode),
      wouldDeliveryBeFree: offers.some(
        (offer) => offer.wouldDeliveryBeFree === true,
      ),
      deliverySavingMinor: offers.reduce(
        (sum, offer) => sum + positive(offer.deliverySavingMinor),
        0,
      ),
    };
  }

  if (
    block?.active !== true &&
    discountMinor === 0 &&
    applied === null &&
    next === null
  ) {
    return CART_MEMBERSHIP_NONE;
  }

  const percent = positive(applied?.percent);
  const missingMinor = positive(next?.missingMinor);
  const nextPercent = positive(next?.percent);

  return {
    kind: 'member',
    discountMinor,
    percent: percent > 0 ? percent : null,
    nextTier:
      missingMinor > 0 && nextPercent > 0
        ? { missingMinor, percent: nextPercent }
        : null,
  };
};

/** The member discount row; only when the discount is above zero. */
export const memberDiscountRow = (
  benefits: CartMembershipBenefits,
): MemberDiscountRow | null => {
  if (benefits.kind !== 'member' || benefits.discountMinor <= 0) {
    return null;
  }
  return {
    line:
      benefits.percent === null
        ? { key: 'membership:cartMemberDiscount' }
        : {
            key: 'membership:cartMemberDiscountPercent',
            params: { percent: benefits.percent },
          },
    amountMinor: benefits.discountMinor,
  };
};

/** "Add X more to get N% member discount"; only for a member below the next step. */
export const nextTierLine = (
  benefits: CartMembershipBenefits,
  money: CartMoney,
): MembershipLine | null =>
  benefits.kind === 'member' && benefits.nextTier
    ? {
        key: 'membership:cartNextTier',
        params: {
          amount: money(benefits.nextTier.missingMinor),
          percent: benefits.nextTier.percent,
        },
      }
    : null;

/** Analytics payload of a shown next-step hint; `null` without one. */
export const tierHintEventPayload = (
  benefits: CartMembershipBenefits,
): MembershipTierHintViewedPayload | null =>
  benefits.kind === 'member' && benefits.nextTier
    ? {
        percent: benefits.nextTier.percent,
        missingMinor: benefits.nextTier.missingMinor,
      }
    : null;

/**
 * Identity of a tier hint view: reaching a new step changes the hinted
 * percent and counts as a new view; a changing missing amount does not.
 */
export const tierHintViewIdentity = (
  payload: MembershipTierHintViewedPayload,
): string => String(payload.percent);

/**
 * Part of the calculate query key that changes whenever member benefits may
 * change: a purchase, a cancellation, a plan switch or `past_due`. `null`
 * without a membership. Other fields (period end, next charge) stay out of it,
 * so a plain `/me` reload does not re-price the cart. `/me` reaches the store
 * unvalidated, hence the tolerant `plan` read: a throw here would stop the
 * activation polling.
 */
export const membershipCartMarker = (
  membership: Membership | null,
): string | null =>
  membership
    ? [
        membership.id,
        membership.plan?.id ?? '',
        membership.status,
        String(membership.benefitsActive),
      ].join('|')
    : null;

/**
 * Delivery fee the club waived on one calculated order, in minor units:
 * calculate's `membership.deliverySavingMinor`, set only when delivery is free
 * thanks to the member threshold and would be paid under the standard one.
 * `0` otherwise, and for a customer without active benefits.
 */
export const orderClubDeliverySavingMinor = (
  order: CartMembershipOrder,
): number => {
  const block = asFields(order.membership);

  return block?.active === true ? positive(block.deliverySavingMinor) : 0;
};

/** A delivery slot as far as its fee goes, all in minor units. */
export interface ClubSlotFee {
  /** Fee of the slot. */
  feeMinor: number;
  /** The zone's own free-delivery threshold the slot comes with. */
  standardThresholdMinor: number;
  /** Order subtotal the threshold is checked against. */
  basketMinor: number;
}

/**
 * Fee of a delivery slot the club waives, in minor units; `0` when the slot
 * is free anyway or stays paid. Slots come from ETA with the standard
 * threshold only, so the member threshold of the calculated order is applied
 * here like calculate applies it: a basket under the standard threshold that
 * reaches the member one gets free delivery.
 */
export const slotClubDeliverySavingMinor = (
  order: CartMembershipOrder,
  slot: ClubSlotFee,
): number => {
  const block = asFields(order.membership);
  const threshold = block?.freeDeliveryThresholdMinor;

  if (
    block?.active !== true ||
    typeof threshold !== 'number' ||
    slot.feeMinor <= 0 ||
    slot.basketMinor >= slot.standardThresholdMinor ||
    slot.basketMinor < threshold
  ) {
    return 0;
  }
  return slot.feeMinor;
};
